import { motion, Reorder } from "framer-motion";
import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { cn } from "@/components/ui/utils";
import { typographyStyles } from "@/components/ui/typography";

const TAB_SURFACE_TRANSITION = "0.1s ease";
const TAB_LABEL_TRANSITION = "color 0.1s ease";
const TAB_UNDERLINE_TRANSITION = { duration: 0.08, ease: "easeOut" as const };

export type AnimatedTabVariant = "pill" | "strip";

export type AnimatedTabItem = {
  id: string;
  label: string | React.ReactNode;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  disabled?: boolean;
  "aria-label"?: string;
  /** Strip variant — double-click label to rename (string labels only). */
  onLabelRename?: (newLabel: string) => void;
  /**
   * Strip variant — marks a transient preview tab: the label is italic, and
   * double-click calls this to keep the tab instead of renaming it.
   */
  onPin?: () => void;
};

/**
 * Original pill tab — do not change underline/label structure (App topbar, node
 * tabs). A button's height, corners and room: ``md`` a bar's (32px, ``sm``'s),
 * ``sm`` a row's (24px, ``xs``'s).
 */
function PillAnimatedTab({
  id,
  label,
  layoutId,
  isSelected,
  onClick,
  offset,
  style,
  isInitialRender,
  size = "md",
}: {
  id?: string;
  label: string | React.ReactNode;
  layoutId: string;
  isSelected: boolean;
  onClick: () => void;
  offset?: number;
  /** The label's own look, over the theme's body text. */
  style?: CSSProperties;
  isInitialRender?: boolean;
  size?: "sm" | "md";
}) {
  const [isHovering, setIsHovering] = useState(false);

  const isStringLabel = typeof label === "string";

  const getThemeStyles = () => {
    if (!isStringLabel) {
      return {};
    }

    return {
      ...(typographyStyles["body-md"] as object),
      color: isSelected
        ? "var(--foreground)"
        : isHovering
          ? "var(--foreground)"
          : "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
    };
  };

  return (
    <div
      data-testid={id ? `tab-${id}` : undefined}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={onClick}
      className={cn(
        "relative cursor-pointer flex items-center [border-width:1px] [border-style:solid] rounded-md border-border bg-transparent [transition:background-color_0.1s_ease,border-color_0.1s_ease] [outline:0px_solid_transparent]! hover:border-border hover:bg-accent hover:text-foreground active:bg-accent",
        size === "sm" ? "h-6 px-2" : "h-8 px-3",
        isSelected && "border-border",
        "shrink-0",
      )}
    >
      <motion.div
        style={{
          width: "max-content",
          transition: TAB_LABEL_TRANSITION,
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          ...getThemeStyles(),
          ...style,
        }}
      >
        {label}
      </motion.div>

      {isSelected && (
        <motion.div
          layoutId={layoutId}
          style={{
            position: "absolute",
            bottom: -offset! - 1,
            left: size === "sm" ? 4 : 6,
            right: size === "sm" ? 4 : 5,
            height: 1,
            transition: "background-color 0.1s ease",
            backgroundColor: "currentColor",
            borderRadius: "2px",
            opacity: 1,
          }}
          transition={
            isInitialRender ? { duration: 0 } : TAB_UNDERLINE_TRANSITION
          }
        />
      )}
    </div>
  );
}

type StripTabSurfaceOptions = {
  isSelected: boolean;
  isHovering: boolean;
  disabled?: boolean;
  size: "sm" | "md";
};

/**
 * A strip tab's surface: its ground, edge and words as it is picked, hovered or
 * out of reach - a button's height, corners and room.
 */
function stripTabSurfaceClassName({
  isSelected,
  isHovering,
  disabled,
  size,
}: StripTabSurfaceOptions) {
  return cn(
    // The selected tab sticks to whichever edge of the scroll area it would
    // otherwise leave through, covering the tabs beneath it, so it is always
    // in view.
    isSelected ? "sticky left-0 right-0 z-1" : "relative",
    "flex items-center gap-0.75 shrink-0 min-w-0 overflow-hidden",
    size === "sm" ? "h-6 max-w-45 px-2" : "h-8 max-w-55 px-3",
    disabled ? "opacity-50" : "opacity-100",
    "[border-width:1px] [border-style:solid] rounded-md",
    isSelected ? "border-border" : "border-transparent",
    isHovering && !disabled
      ? "bg-accent"
      : isSelected
        ? "bg-background"
        : "bg-transparent",
    isSelected ? "shadow-xs" : "[box-shadow:none]",
    isSelected || isHovering ? "text-foreground" : "text-muted-foreground/80",
    "[transition:background-color_0.1s_ease,color_0.1s_ease,box-shadow_0.1s_ease,border-color_0.1s_ease]",
    "active:bg-accent",
    "focus-visible:[outline:2px_solid_var(--primary)] focus-visible:[outline-offset:1px]",
    "[&:hover_.animated-tab-close]:opacity-100",
  );
}

/**
 * A reorderable tab's hover, press and focus, as Emotion's global rules had
 * them over its inline surface (its press's ground loses to that surface's
 * own, as it did).
 */
const STRIP_TAB_DRAGGABLE_CLASS =
  "[&:hover_.animated-tab-close]:opacity-100 [&:active:not(:focus-visible)]:bg-accent focus-visible:[outline:2px_solid_var(--primary)] focus-visible:[outline-offset:1px]";

function stripTabSurfaceInlineStyle(
  options: StripTabSurfaceOptions,
): React.CSSProperties {
  const { isSelected, isHovering, disabled, size } = options;
  return {
    listStyle: "none",
    flexShrink: 0,
    // Sticky when selected — see ``stripTabSurfaceSx``.
    position: isSelected ? "sticky" : "relative",
    left: isSelected ? 0 : undefined,
    right: isSelected ? 0 : undefined,
    zIndex: isSelected ? 1 : undefined,
    // A tab is clicked - and double-clicked to rename - so it points; the
    // closed hand is for while it is carried (``StripReorderableTab``).
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: "3px",
    height: size === "sm" ? 24 : 32,
    opacity: disabled ? 0.5 : 1,
    minWidth: 0,
    overflow: "hidden",
    border: "1px solid",
    borderColor: isSelected ? "var(--border)" : "transparent",
    borderRadius: "calc(var(--radius) - 2px)",
    backgroundColor:
      isHovering && !disabled
        ? "var(--accent)"
        : isSelected
          ? "var(--background)"
          : "transparent",
    boxShadow: isSelected ? "var(--shadow-xs)" : "none",
    color:
      isSelected || isHovering
        ? "var(--foreground)"
        : "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
    maxWidth: size === "sm" ? 180 : 220,
    paddingLeft: size === "sm" ? 8 : 12,
    paddingRight: size === "sm" ? 8 : 12,
    transition: `background-color ${TAB_SURFACE_TRANSITION}, color ${TAB_SURFACE_TRANSITION}, box-shadow ${TAB_SURFACE_TRANSITION}, border-color ${TAB_SURFACE_TRANSITION}`,
  };
}

/** Thread-panel strip tabs (separate from pill underline behavior). */
function StripAnimatedTab({
  id,
  label,
  isSelected,
  onClick,
  style,
  size = "md",
  startDecorator,
  endDecorator,
  ariaLabel,
  disabled = false,
  onLabelRename,
  onPin,
  chromeless = false,
  isHovering: isHoveringProp = false,
}: {
  id?: string;
  label: string | React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  /** The label's own look, over the theme's body text. */
  style?: CSSProperties;
  size?: "sm" | "md";
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  onLabelRename?: (newLabel: string) => void;
  onPin?: () => void;
  /** Surface chrome (border, shadow) is on the parent ``Reorder.Item``. */
  chromeless?: boolean;
  isHovering?: boolean;
}) {
  const [isHoveringLocal, setIsHoveringLocal] = useState(false);
  const isHovering = chromeless ? isHoveringProp : isHoveringLocal;
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editLabelValue, setEditLabelValue] = useState("");
  const labelInputRef = useRef<HTMLInputElement>(null);
  const isStringLabel = typeof label === "string";
  const canRenameLabel = isStringLabel && Boolean(onLabelRename) && !disabled;

  useEffect(() => {
    if (!isEditingLabel) return;
    const input = labelInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [isEditingLabel]);

  const commitLabelRename = useCallback(() => {
    if (!canRenameLabel) {
      setIsEditingLabel(false);
      return;
    }
    const trimmed = editLabelValue.trim();
    if (trimmed && trimmed !== label) {
      onLabelRename?.(trimmed);
    }
    setIsEditingLabel(false);
  }, [canRenameLabel, editLabelValue, label, onLabelRename]);

  const startLabelRename = useCallback(
    (event: React.MouseEvent) => {
      if (!canRenameLabel) return;
      event.stopPropagation();
      event.preventDefault();
      setEditLabelValue(label as string);
      setIsEditingLabel(true);
    },
    [canRenameLabel, label],
  );

  const getThemeStyles = () => {
    if (!isStringLabel) {
      return {};
    }

    return {
      ...(typographyStyles["body-md"] as object),
      color: isSelected
        ? "var(--foreground)"
        : isHovering
          ? "var(--foreground)"
          : "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
    };
  };

  const labelTitle =
    ariaLabel ?? (isStringLabel ? (label as string) : undefined);

  const labelStyle = {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: isEditingLabel ? "clip" : "ellipsis",
    whiteSpace: "nowrap",
    transition: TAB_LABEL_TRANSITION,
    display: "block",
    ...getThemeStyles(),
    ...style,
    ...(onPin && { fontStyle: "italic" }),
    ...(isEditingLabel && {
      border: "none",
      background: "transparent",
      outline: "none",
      padding: 0,
      margin: 0,
      font: "inherit",
      letterSpacing: "inherit",
      width: "100%",
      minWidth: 48,
      cursor: "text",
      boxShadow: "none",
      appearance: "none",
    }),
  } as CSSProperties;
  const LabelElement = isEditingLabel ? "input" : "span";

  const handleLabelKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      commitLabelRename();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setEditLabelValue(label as string);
      setIsEditingLabel(false);
    }
  };

  const name = isStringLabel ? (
    <LabelElement
      ref={isEditingLabel ? labelInputRef : undefined}
      value={isEditingLabel ? editLabelValue : undefined}
      onChange={
        isEditingLabel
          ? (e: React.ChangeEvent<HTMLInputElement>) =>
              setEditLabelValue(e.target.value)
          : undefined
      }
      onBlur={isEditingLabel ? commitLabelRename : undefined}
      onKeyDown={isEditingLabel ? handleLabelKeyDown : undefined}
      // A preview tab's double-click keeps it; a kept tab's renames it.
      onDoubleClick={isEditingLabel ? undefined : (onPin ?? startLabelRename)}
      onPointerDown={isEditingLabel ? (e) => e.stopPropagation() : undefined}
      onClick={isEditingLabel ? (e) => e.stopPropagation() : undefined}
      data-testid={isEditingLabel && id ? `tab-rename-${id}` : undefined}
      style={labelStyle}
    >
      {!isEditingLabel ? label : null}
    </LabelElement>
  ) : (
    <div className="flex-1 min-w-0 flex items-center overflow-hidden">
      {label}
    </div>
  );
  // A tab's way to close it floats in over the end of its name under the
  // pointer, on the tab's hover colour with a fade before it, rather than
  // holding room of its own beside the name: the tab is as wide as its name,
  // and the name keeps its width. The name is still what is picked up to drag.
  const isCloseShown = isHovering && !disabled;
  const content = (
    <>
      {startDecorator}
      {endDecorator && !isEditingLabel ? (
        <div className="relative flex items-center min-w-0 max-w-full">
          {name}
          <div
            // Into the tab's own padding, so it covers less of the name - as
            // far in from its end as from its top.
            className={cn(
              "absolute -right-2 top-[50%] flex items-center pl-2 [background:linear-gradient(to_right,transparent,var(--accent)_8px)]",
              isCloseShown ? "opacity-[1]" : "opacity-[0]",
              isCloseShown ? "pointer-events-auto" : "pointer-events-none",
              isCloseShown
                ? "transform-[translate(0px,-50%)]"
                : "transform-[translate(6px,-50%)]",
              "[transition:opacity_150ms_ease,transform_150ms_ease]",
            )}
          >
            {endDecorator}
          </div>
        </div>
      ) : (
        <>
          {name}
          {endDecorator}
        </>
      )}
    </>
  );

  if (chromeless) {
    return (
      <div
        className={cn(
          "flex items-center gap-0.75 flex-1 min-w-0 max-w-full h-full",
          isEditingLabel ? "cursor-text" : "cursor-[inherit]",
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      role="tab"
      aria-selected={isSelected}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      title={labelTitle}
      data-testid={id ? `tab-${id}` : undefined}
      onMouseEnter={() => setIsHoveringLocal(true)}
      onMouseLeave={() => setIsHoveringLocal(false)}
      onClick={disabled || isEditingLabel ? undefined : onClick}
      className={cn(
        stripTabSurfaceClassName({ isSelected, isHovering, disabled, size }),
        // A tab is clicked - and double-clicked to rename - so it points; the
        // closed hand is for while it is carried (``StripReorderableTab``).
        disabled
          ? "cursor-not-allowed"
          : isEditingLabel
            ? "cursor-text"
            : "cursor-pointer",
      )}
    >
      {content}
    </div>
  );
}

function StripReorderableTab(
  props: React.ComponentProps<typeof StripAnimatedTab> & { value: string },
) {
  const {
    value,
    id,
    isSelected,
    onClick,
    disabled,
    size = "md",
    ariaLabel,
    ...tabProps
  } = props;
  const [isHovering, setIsHovering] = useState(false);
  // Held, it wears the closed hand until it is let go: ``:active`` is lost as
  // soon as the drag moves it, and its own open hand showed through. The page
  // wears it too, for wherever the pointer strays while it is carried.
  const [isDragging, setIsDragging] = useState(false);
  const wasPageCursor = useRef("");

  return (
    <Reorder.Item
      value={value}
      className={STRIP_TAB_DRAGGABLE_CLASS}
      style={{
        ...stripTabSurfaceInlineStyle({
          isSelected,
          isHovering,
          disabled,
          size,
        }),
        ...(isDragging && { cursor: "grabbing" }),
      }}
      onDragStart={() => {
        setIsDragging(true);
        wasPageCursor.current = document.body.style.cursor;
        document.body.style.cursor = "grabbing";
      }}
      onDragEnd={() => {
        setIsDragging(false);
        document.body.style.cursor = wasPageCursor.current;
      }}
      role="tab"
      tabIndex={disabled ? -1 : 0}
      aria-selected={isSelected}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      data-testid={id ? `tab-${id}` : undefined}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={disabled ? undefined : onClick}
      whileDrag={{
        zIndex: 10,
        scale: 1.02,
        ...(isSelected ? { boxShadow: "var(--shadow-xs)" } : {}),
      }}
    >
      <StripAnimatedTab
        {...tabProps}
        id={id}
        isSelected={isSelected}
        onClick={onClick}
        disabled={disabled}
        size={size}
        ariaLabel={ariaLabel}
        chromeless
        isHovering={isHovering}
      />
    </Reorder.Item>
  );
}

/** Horizontal scroll with hidden native scrollbar and edge fades. */
function StripTabsScrollArea({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  // Each fade, set in from its edge past the selected tab where that tab is
  // stuck there, so it falls on the tabs folding behind it rather than on the
  // one pinned over them - moved in the scroll's own frame, not a render
  // after it, or the fade sat over the pinned tab for a frame.
  const leftFadeRef = useRef<HTMLDivElement>(null);
  const rightFadeRef = useRef<HTMLDivElement>(null);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const overflow = el.scrollWidth - el.clientWidth;
    const hasOverflow = overflow > 2;

    if (!hasOverflow) {
      if (el.scrollLeft !== 0) {
        el.scrollLeft = 0;
      }
      setShowLeftFade(false);
      setShowRightFade(false);
      return;
    }

    const atStart = el.scrollLeft <= 2;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
    setShowLeftFade(!atStart);
    setShowRightFade(!atEnd);
    // The selected tab, stuck (``position: sticky``) to an edge it would
    // have scrolled out through: at that edge while tabs scroll on beneath it
    // - or past it, as the scroll's rubber band carries it at the end.
    const selected = el.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    );
    const area = el.getBoundingClientRect();
    const tab = selected?.getBoundingClientRect();
    if (leftFadeRef.current) {
      leftFadeRef.current.style.left = `${
        tab && !atStart && tab.left <= area.left + 1 ? tab.width : 0
      }px`;
    }
    if (rightFadeRef.current) {
      rightFadeRef.current.style.right = `${
        tab && !atEnd && tab.right >= area.right - 1 ? tab.width : 0
      }px`;
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScroll();
    const observer = new ResizeObserver(updateScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScroll, children]);

  return (
    <div className="relative flex-1 min-w-0 h-full">
      <div
        ref={scrollRef}
        onScroll={updateScroll}
        className="flex items-center h-full min-h-full overflow-x-auto overflow-y-visible [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      <div
        aria-hidden
        ref={leftFadeRef}
        data-testid="strip-tabs-fade-left"
        className={cn(
          "absolute top-0 left-0 bottom-0 w-10 pointer-events-none",
          showLeftFade ? "opacity-[1]" : "opacity-[0]",
          "[transition:opacity_0.15s_ease] [background:linear-gradient(to_right,var(--background),transparent)]",
        )}
      />
      <div
        aria-hidden
        ref={rightFadeRef}
        data-testid="strip-tabs-fade-right"
        className={cn(
          "absolute top-0 right-0 bottom-0 w-10 pointer-events-none",
          showRightFade ? "opacity-[1]" : "opacity-[0]",
          "[transition:opacity_0.15s_ease] [background:linear-gradient(to_right,transparent,var(--background))]",
        )}
      />
    </div>
  );
}

const AnimatedTabs = memo(function AnimatedTabs({
  tabs,
  selectedTabId,
  onTabChange,
  offset = -5.5,
  tabStyle,
  size = "md",
  align = "center",
  rootClassName,
  variant = "pill",
  trailingAction,
  scrollable = false,
  reorderable = false,
  onTabsReorder,
}: {
  tabs: AnimatedTabItem[];
  selectedTabId: string;
  onTabChange: (tabId: string) => void;
  offset?: number;
  /** Each tab's label's own look, over the theme's body text. */
  tabStyle?: CSSProperties;
  size?: "sm" | "md";
  align?: "start" | "center";
  /** The tabs' row's own look, over its own. */
  rootClassName?: string;
  variant?: AnimatedTabVariant;
  trailingAction?: React.ReactNode;
  /** Strip variant only — scroll when tabs overflow (without a trailing action). */
  scrollable?: boolean;
  /** Strip variant only — drag tabs to reorder (requires ``onTabsReorder``). */
  reorderable?: boolean;
  onTabsReorder?: (orderedTabIds: string[]) => void;
}) {
  const [localSelectedId, setLocalSelectedId] = useState(
    selectedTabId != null ? selectedTabId : tabs[0]?.id,
  );
  const [isInitialRender, setIsInitialRender] = useState(true);
  const layoutId = useId();
  const isStrip = variant === "strip";

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialRender(false);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  useLayoutEffect(() => {
    if (selectedTabId !== localSelectedId) {
      setLocalSelectedId(selectedTabId);
    }
  }, [localSelectedId, selectedTabId]);

  const createHandleTabClick = useCallback(
    (tabId: string, disabled?: boolean) => () => {
      if (disabled) return;
      setLocalSelectedId(tabId);
      onTabChange(tabId);
    },
    [onTabChange],
  );

  const stripReorderEnabled =
    isStrip && reorderable && Boolean(onTabsReorder) && tabs.length > 1;
  const tabIds = tabs.map((tab) => tab.id);

  const handleTabsReorder = useCallback(
    (newOrder: string[]) => {
      onTabsReorder?.(newOrder);
    },
    [onTabsReorder],
  );

  const renderStripTab = (tab: AnimatedTabItem) => {
    const isSelected = tab.id === localSelectedId;
    const tabProps = {
      id: tab.id,
      label: tab.label,
      startDecorator: tab.startDecorator,
      endDecorator: tab.endDecorator,
      ariaLabel: tab["aria-label"],
      disabled: tab.disabled,
      reorderable: stripReorderEnabled,
      isSelected,
      onClick: createHandleTabClick(tab.id, tab.disabled),
      onLabelRename: tab.onLabelRename,
      onPin: tab.onPin,
      style: tabStyle,
      size,
    };

    if (stripReorderEnabled) {
      return <StripReorderableTab key={tab.id} value={tab.id} {...tabProps} />;
    }

    return <StripAnimatedTab key={tab.id} {...tabProps} />;
  };

  const tabListClassName = cn(
    "inline-flex pt-0 pb-0 items-center shrink-0 w-max align-top",
    // The bar's own room before the first: none of its own on top of it.
    isStrip ? "gap-0.5 pl-0 pr-0 h-auto" : "gap-1 h-full",
    align === "start" ? "justify-start" : "justify-center",
    (!isStrip || !trailingAction) && rootClassName,
  );

  const tabList = stripReorderEnabled ? (
    <>
      <Reorder.Group
        axis="x"
        layoutScroll
        role="tablist"
        values={tabIds}
        onReorder={handleTabsReorder}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          margin: 0,
          padding: 0,
          listStyle: "none",
          width: "max-content",
        }}
      >
        {tabs.map((tab) => renderStripTab(tab))}
      </Reorder.Group>
    </>
  ) : (
    <div role={isStrip ? "tablist" : undefined} className={tabListClassName}>
      {tabs.map((tab) =>
        isStrip ? (
          renderStripTab(tab)
        ) : (
          <PillAnimatedTab
            key={tab.id}
            id={tab.id}
            layoutId={layoutId}
            label={tab.label}
            isSelected={tab.id === localSelectedId}
            onClick={createHandleTabClick(tab.id, tab.disabled)}
            offset={offset}
            style={tabStyle}
            isInitialRender={isInitialRender}
            size={size}
          />
        ),
      )}
    </div>
  );

  const stripScrollEnabled = isStrip && (Boolean(trailingAction) || scrollable);

  if (!stripScrollEnabled) {
    return tabList;
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1 w-full h-full pl-0 pr-0 overflow-visible",
        rootClassName,
      )}
    >
      <StripTabsScrollArea>{tabList}</StripTabsScrollArea>
      {trailingAction}
    </div>
  );
});

export { AnimatedTabs };
